export namespace StructuralData {
    export enum Type {
        website = 'Website',
        webpage = 'WebPage',
        organization = 'Organization',
        corporation = 'Corporation',
        person = 'Person',
        search = 'SearchAction',
        article = 'article',
        newArticle = 'newsArticle',
        qaPage = 'QAPage',
        faqPage = 'FAQPage',
        question = 'Question',
        answer = 'Answer',
        product = 'Product',
        productGroup = 'ProductGroup',
        listing = 'ItemList',
        listItem = 'ListItem',
        aggregateRating = 'AggregateRating',
        rating = 'Rating',
        thing = 'Thing',
        brand = 'Brand',
        offer = 'Offer',
        review = 'Review',
        store = 'Store',
        address = 'PostalAddress',
        openingHours = 'OpeningHoursSpecification',
        geo = 'GeoCoordinates',
        breadcrumbList = 'BreadcrumbList',
        breadcrumbItem = 'ListItem',
        contactPoint = 'ContactPoint',
        imageObject = 'ImageObject',
        videoObject = 'VideoObject',
        howTo = 'HowTo',
        howToSupply = 'HowToSupply',
        howToTool = 'HowToTool',
        howToStep = 'HowToStep',
        howToDirection = 'HowToDirection',
        howToTip = 'HowToTip',
        monetaryAmount = 'MonetaryAmount',
        jewelryStore = 'JewelryStore'
    }

    export enum ItemCondition {
        used = 'https://schema.org/UsedCondition',
        new = 'https://schema.org/NewCondition',
        damaged = 'https://schema.org/DamagedCondition',
        refurbished = 'https://schema.org/RefurbishedCondition'
    }

    export enum ItemAvailability {
        discontinued = 'https://schema.org/Discontinued',
        inStock = 'https://schema.org/InStock',
        inStoreOnly = 'https://schema.org/InStoreOnly',
        limitedAvailability = 'https://schema.org/LimitedAvailability',
        onlineOnly = 'https://schema.org/OnlineOnly',
        outOfStock = 'https://schema.org/OutOfStock',
        preOrder = 'https://schema.org/PreOrder',
        preSale = 'https://schema.org/PreSale',
        soldOut = 'https://schema.org/SoldOut'
    }

    export interface IData<T extends Type | string = string> {
        '@type': T;
    }

    export interface IAppData extends IData<Type.website> {
        '@context': 'http://schema.org';
        url: string;
        name?: string;
        potentialAction: IPotentialActionData;
    }

    export interface IWebpageData extends IData<Type.webpage> {
        '@id': string;
    }

    export interface IPotentialActionData extends IData<Type.search> {
        target: string;
        'query-input': string;
    }

    export interface IOrganizationData extends IData<Type.organization | Type.corporation> {
        '@context'?: 'http://schema.org';
        sameAs?: string[];
        url?: string;
        name: string;
        legalName?: string;
        logo?: string | IImageObjectData;
        foundingDate?: string;
        founders?: IPersonData[];
        address?: IAddressData;
        contactPoint?: IContactPoint;
        aggregateRating?: IAggregateRatingData;
        review?: IReviewData;
        telephone?: string;
        description?: string;
        email?: string;
    }

    export interface IContactPoint extends IData<Type.contactPoint> {
        contactType: string;
        telephone?: string;
        email?: string;
    }

    export interface IPersonData extends IData<Type.person> {
        name: string;
    }

    export interface IImageObjectData extends IData<Type.imageObject> {
        url: string;
        height?: string;
        width?: string;
    }

    export interface IVideoObjectData extends IData<Type.videoObject> {
        name?: string;
        description?: string;
        thumbnailUrl?: string;
        contentUrl?: string;
        embedUrl?: string;
        uploadDate?: string;
        duration?: string;
    }

    export interface IArticleData extends IData<Type.article> {
        '@context': 'http://schema.org';
        mainEntityOfPage?: IWebpageData;
        headline?: string;
        alternativeHeadline?: string;
        datePublished?: string;
        dateModified?: string;
        dateCreated?: string;
        author?: IPersonData | IOrganizationData;
        image?: string | IImageObjectData;
        publisher?: IOrganizationData;
        award?: string;
        editor?: IPersonData;
        genre?: string;
        keywords?: string;
        wordCount?: number;
        url?: IWebpageData;
        description?: string;
        articleBody?: string;
    }

    export interface IHowToData extends IData<Type.howTo> {
        '@context': 'http://schema.org';
        name?: string;
        description?: string;
        image?: string | IImageObjectData;
        estimatedCost?: string | IMonetaryAmount;
        supply?: IHowToSupplyData[];
        tool?: IHowToTool[];
        totalTime?: string;
        video?: IVideoObjectData;
    }

    export interface IHowToStep extends IData<Type.howToStep> {
        url?: string;
        name?: string;
        itemListElement?: (IHowToDirection | IHowToTip)[];
        image?: string | IImageObjectData;
        text?: string;
    }

    export interface IHowToDirection extends IData<Type.howToDirection> {
        text: string;
    }

    export interface IHowToTip extends IData<Type.howToTip> {
        text: string;
    }

    export interface IHowToTool extends IData<Type.howToTool> {
        name: string;
    }

    export interface IHowToSupplyData extends IData<Type.howToSupply> {
        name: string;
    }

    export interface IMonetaryAmount extends IData<Type.monetaryAmount> {
        currency: string;
        value: string;
    }

    export interface IQAPageData extends IData<Type.qaPage> {
        '@context': 'http://schema.org';
    }

    export interface IFAQPageData extends IData<Type.faqPage> {
        '@context': 'http://schema.org';
        mainEntity: IQuestionData[];
    }

    export interface IQuestionData extends IData<Type.question> {
        name: string;
        acceptedAnswer: IAnswerData;
    }

    export interface IAnswerData extends IData<Type.answer> {
        text: string;
    }

    export interface IProductGroupData extends IData<Type.productGroup>, IProductDetailsData {
        '@context': 'http://schema.org';
        productGroupID?: string;
        variesBy: string[];
        hasVariant: (IProductData & { [key: string]: string })[];
    }

    export interface IProductData extends IData<Type.product>, IProductDetailsData {
        '@context': 'http://schema.org';
    }

    export interface IProductDetailsData {
        '@id'?: string;
        name?: string;
        sku?: string;
        url?: string;
        image?: string[] | IImageObjectData;
        description?: string;
        aggregateRating?: IAggregateRatingData;
        review?: IReviewData;
        brand?: IBrandData;
        offers?: IOffersData;
        mpn?: string;
        type_id?: string;
    }

    export interface IListingData extends IData<Type.listing> {
        '@context': 'http://schema.org';
        url?: string;
        numberOfItems?: string;
        itemListElement?: IItemListData[];
    }

    export interface IItemListData extends IData<Type.listItem> {
        position?: number;
        item?: IProductData;
    }

    export interface IProductLisingData extends IData<Type.product> {
        image?: string;
        url?: string;
        name?: string;
        offers?: IOffersData;
    }

    export interface IAggregateRatingData extends IData<Type.aggregateRating> {
        ratingValue?: number;
        bestRating?: number;
        worstRating?: number;
        reviewCount?: number;
    }

    export interface IBrandData extends IData<Type.brand> {
        name: string;
    }

    export interface IOffersData extends IData<Type.offer> {
        priceCurrency?: string;
        price?: string;
        priceValidUntil?: string;
        itemCondition?: ItemCondition;
        availability?: ItemAvailability;
        seller?: IOrganizationData;
    }

    export interface IReviewData extends IData<Type.review> {
        author?: string | IPersonData;
        name?: string;
        reviewBody?: string;
        publisher?: IOrganizationData;
        datePublished?: string;
        description?: string;
        reviewRating?: IRatingData;
    }

    export interface IRatingData extends IData<Type.rating> {
        bestRating?: number;
        ratingValue?: number;
        worstRating?: number;
    }

    export interface IBreadcrumbsListData extends IData<Type.breadcrumbList> {
        '@context': 'http://schema.org';
        itemListElement: IBreadcrumbsItemData[];
    }

    export interface IBreadcrumbsItemData extends IData<Type.breadcrumbItem> {
        position?: number;
        name?: string;
        item?: string | IBreadcrumbsItemItemData;
    }

    export interface IBreadcrumbsItemItemData {
        '@id': string;
        name?: string;
        image?: string | IImageObjectData;
    }

    export interface IPlacedElement {
        // hashed data (just json string of data)
        json: string;
        // script element
        script: HTMLScriptElement;
    }
    export interface IPhoneNumber {
        no: string;
        type: string;
    }

    export interface ILocalBusinessData extends IData<Type.store | Type.jewelryStore> {
        '@context': 'http://schema.org';
        image?: string[];
        '@id'?: string;
        name?: string;
        address?: IAddressData;
        review?: IReviewData;
        geo?: IGeoData;
        url?: string;
        telephone?: string;
        openingHoursSpecification?: IOpeningHoursSpecificationData[];
    }

    export interface IAddressData extends IData<Type.address> {
        streetAddress?: string;
        addressLocality?: string;
        addressRegion?: string;
        postalCode?: string;
        addressCountry?: string;
    }

    export interface IGeoData extends IData<Type.geo> {
        latitude: number;
        longitude: number;
    }

    export interface IOpeningHoursSpecificationData extends IData<Type.openingHours> {
        dayOfWeek: string[] | string;
        opens: string;
        closes: string;
    }
}
